import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityMetric } from '../../../components/entity-detail/EntityMetric.js';
import { EntityFieldList } from '../../../components/entity-detail/EntityFieldList.js';
import { LinkCell, type OwnLink } from '../../../shared/ui/LinkCell.js';
import type { EntityKind } from '../../../shared/ui/EntityLink.js';
import { formatDateTime, taskDueState } from '../../../shared/format/entity-detail.js';
import { formatPersonName } from '../../../shared/format/index.js';
import type { TaskItem } from '../api/tasks.client.js';
import {
  getTaskPriorityLabel,
  getTaskStatusLabel,
  PRIORITY_TONE,
  STATUS_TONE,
} from '../lib/taskMeta.js';

export function TaskDetailSidebar({ task }: { task: TaskItem }): ReactElement {
  const { t } = useTranslation();
  const prefix = 'crm.features.features.tasks.pages.taskcardpage.';
  const refs: { key: EntityKind; own: OwnLink | null }[] = [
    { key: 'deal', own: task.deal ? { id: task.deal.id, label: task.deal.title } : null },
    {
      key: 'contact',
      own: task.contact
        ? {
            id: task.contact.id,
            label: formatPersonName(task.contact.firstName, task.contact.lastName),
          }
        : null,
    },
    {
      key: 'company',
      own: task.company ? { id: task.company.id, label: task.company.name } : null,
    },
    {
      key: 'document',
      own: task.document
        ? { id: task.document.id, label: task.document.title }
        : task.documentId
          ? { id: task.documentId, label: t('crm.shared.entity_type.document') }
          : null,
    },
    {
      key: 'project',
      own: task.project ? { id: task.project.id, label: task.project.name } : null,
    },
  ];
  // Срок в сводке — обычным значением (дата), а просрочка — подсказкой под ней:
  // длинный чип «Просрочено с …» не помещался в половину ширины сайдбара.
  const closed = task.status === 'done' || task.status === 'cancelled';
  const dueOverdue = !closed && taskDueState(task.dueAt) === 'overdue';
  const overdueDays =
    dueOverdue && task.dueAt
      ? Math.max(1, Math.floor((Date.now() - new Date(task.dueAt).getTime()) / 86_400_000))
      : 0;
  const member = task.createdByMember?.user;
  const fields = [
    ...(member
      ? [
          {
            key: 'author',
            label: t('crm.features.task_detail.author'),
            value: member.name || member.email,
          },
        ]
      : []),
    { key: 'created', label: t(`${prefix}создана`), value: formatDateTime(task.createdAt) },
    {
      key: 'updated',
      label: t('crm.features.task_detail.updated'),
      value: formatDateTime(task.updatedAt),
    },
    ...(task.completedAt
      ? [
          {
            key: 'completed',
            label: t('crm.features.task_detail.completed'),
            value: formatDateTime(task.completedAt),
          },
        ]
      : []),
  ];
  return (
    <>
      <section className="entity-panel">
        <h2 className="entity-section-title">{t('crm.features.company_detail.summary')}</h2>
        <div className="task-detail-metrics">
          <EntityMetric
            label={t(`${prefix}срок`)}
            testId="task-metric-due"
            value={
              task.dueAt ? (
                <span className={dueOverdue ? 'task-detail-due-overdue' : undefined}>
                  {formatDateTime(task.dueAt)}
                </span>
              ) : (
                '—'
              )
            }
            hint={dueOverdue ? t('crm.entity_detail.overdue_days', { count: overdueDays }) : null}
          />
          <EntityMetric
            label={t('crm.features.task_detail.status')}
            value={
              <EntityStatusChip tone={STATUS_TONE[task.status]}>
                {getTaskStatusLabel(task.status)}
              </EntityStatusChip>
            }
          />
          <EntityMetric
            label={t('crm.features.task_detail.priority')}
            value={
              <EntityStatusChip tone={PRIORITY_TONE[task.priority]}>
                {getTaskPriorityLabel(task.priority)}
              </EntityStatusChip>
            }
          />
          <EntityMetric
            label={t(`${prefix}исполнитель`)}
            exact={
              task.assigneeMember?.user.name ||
              task.assigneeMember?.user.email ||
              t('crm.features.task_detail.unassigned')
            }
            value={
              task.assigneeMember?.user.name ||
              task.assigneeMember?.user.email ||
              t('crm.features.task_detail.unassigned')
            }
          />
        </div>
      </section>
      <section className="entity-panel">
        <h2 className="entity-section-title">{t('crm.features.task_detail.links')}</h2>
        <EntityFieldList
          fields={refs
            .filter((ref) => ref.own || task.inheritedLinks?.[ref.key])
            .map((ref) => {
              const inherited = task.inheritedLinks?.[ref.key];
              return {
                key: ref.key,
                label: t(`crm.shared.entity_type.${ref.key}`),
                value: (
                  <>
                    <LinkCell kind={ref.key} own={ref.own} inherited={inherited} />
                    {!ref.own && inherited ? (
                      <span className="entity-meta">
                        {' '}
                        · {t(`crm.shared.inherited_via.${inherited.via}`)}
                      </span>
                    ) : null}
                  </>
                ),
              };
            })}
        />
      </section>
      <section className="entity-panel">
        <h2 className="entity-section-title">{t('crm.features.company_detail.details')}</h2>
        <EntityFieldList fields={fields} />
      </section>
    </>
  );
}
